/**
 * GenerationOemConfirmationDialog — record an engineer's reconciliation of an
 * OEM rating conflict.
 *
 * Some catalog entries publish irreconcilable figures for the same machine
 * (Siemens SCC6-5000F: 391 MW net, 398 MW gross, 387 MW overview). The platform
 * refuses to pick one, because whichever it chose would flow into duty, N-1,
 * water and footprint as though it were established fact.
 *
 * This is the way through, and it is deliberately shaped as EVIDENCE rather
 * than consent: the engineer states the reconciled figure, which basis it is
 * on, where it came from, and who is attesting to it. A form that only asked
 * "are you sure?" would be a "use anyway" button wearing a costume — the exact
 * failure mode this gate exists to prevent.
 */
import React, { useState } from 'react';
import { AlertTriangle, ShieldCheck, X } from 'lucide-react';

import { validateEquipmentRatingConfirmation } from '../../../lib/generationImpact/generationEquipmentCatalog';
import type {
  GenerationEquipmentRatingConfirmation,
  GenerationEquipmentRatingScope,
} from '../../../types/generationImpact';

export interface GenerationOemConfirmationDialogProps {
  catalogId: string;
  manufacturer: string;
  model: string;
  /** The vendor's own words about what does not reconcile — shown verbatim. */
  conflictNote: string;
  /** Prefills the figure field with the entry's published headline rating. */
  publishedOutputMW?: number;
  publishedRatingScope?: GenerationEquipmentRatingScope;
  onCancel: () => void;
  onConfirm: (confirmation: GenerationEquipmentRatingConfirmation) => void;
}

const LABEL = 'block text-[10px] font-bold uppercase tracking-wider text-text-muted';
const FIELD = 'mt-1 w-full rounded-md border border-border bg-background p-2 text-xs text-text-primary';

export const GenerationOemConfirmationDialog: React.FC<GenerationOemConfirmationDialogProps> = ({
  catalogId,
  manufacturer,
  model,
  conflictNote,
  publishedOutputMW,
  publishedRatingScope,
  onCancel,
  onConfirm,
}) => {
  const [outputMW, setOutputMW] = useState(
    publishedOutputMW != null ? String(publishedOutputMW) : '',
  );
  const [scope, setScope] = useState<GenerationEquipmentRatingScope>(
    publishedRatingScope ?? 'net-plant',
  );
  const [sourceReference, setSourceReference] = useState('');
  const [confirmedBy, setConfirmedBy] = useState('');
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const draft: GenerationEquipmentRatingConfirmation = {
    catalogId,
    confirmedOutputMW: Number(outputMW),
    confirmedRatingScope: scope,
    sourceReference,
    confirmedBy,
    confirmedAt: new Date().toISOString(),
    ...(note.trim() ? { note: note.trim() } : {}),
  };
  // Same validator the gate uses — the dialog cannot admit anything the
  // library would reject.
  const errors = validateEquipmentRatingConfirmation(draft, catalogId);

  const submit = (): void => {
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    onConfirm({ ...draft, confirmedAt: new Date().toISOString() });
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Reconcile the published rating for ${manufacturer} ${model}`}
      data-testid="generation-oem-confirmation-dialog"
      onKeyDown={(event) => { if (event.key === 'Escape') onCancel(); }}
    >
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-2xl">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="text-xs font-bold uppercase tracking-wider text-text-primary">
              Reconcile the published rating
            </h2>
            <p className="mt-0.5 text-[11px] text-text-secondary">{manufacturer} {model}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="shrink-0 rounded-md border border-border p-1 text-text-muted hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>

        {/* The vendor's own contradiction, verbatim — the user cannot resolve
            what they cannot see. */}
        <p className="mt-3 rounded-md border-l-2 border-warning bg-warning/5 p-2 text-[10px] leading-relaxed text-text-secondary">
          {conflictNote}
        </p>

        <p className="mt-3 text-[10px] leading-relaxed text-text-muted">
          Record the figure this project will engineer against, and the evidence for it.
          The catalog entry stays flagged; this attestation is saved with the scenario and
          replays with it.
        </p>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <label className={LABEL}>Confirmed output (MW)
            <input
              type="number"
              value={outputMW}
              onChange={(event) => setOutputMW(event.target.value)}
              step="0.1"
              className={FIELD}
              data-testid="oem-confirm-output"
            />
          </label>
          <label className={LABEL}>Rating basis
            <select
              value={scope}
              onChange={(event) => setScope(event.target.value as GenerationEquipmentRatingScope)}
              className={FIELD}
              data-testid="oem-confirm-scope"
            >
              <option value="net-plant">Net plant</option>
              <option value="gross-plant">Gross plant</option>
              <option value="generator-terminals">Generator terminals</option>
            </select>
          </label>
        </div>

        <label className={`${LABEL} mt-2`}>Source reference
          <input
            value={sourceReference}
            onChange={(event) => setSourceReference(event.target.value)}
            placeholder="Vendor email, quote number, or signed datasheet"
            className={FIELD}
            data-testid="oem-confirm-source"
          />
        </label>

        <label className={`${LABEL} mt-2`}>Confirmed by
          <input
            value={confirmedBy}
            onChange={(event) => setConfirmedBy(event.target.value)}
            placeholder="Name or email of the confirming engineer"
            className={FIELD}
            data-testid="oem-confirm-by"
          />
        </label>

        <label className={`${LABEL} mt-2`}>Note (optional)
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder="What was reconciled, and how"
            className={FIELD}
            data-testid="oem-confirm-note"
          />
        </label>

        {showErrors && errors.length > 0 && (
          <ul
            className="mt-3 space-y-1 rounded-md border border-danger/50 bg-danger/5 p-2"
            data-testid="oem-confirm-errors"
          >
            {errors.map(error => (
              <li key={error} className="flex items-start gap-1.5 text-[10px] text-danger">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                {error}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:text-text-primary"
            data-testid="oem-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-md border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5"
            data-testid="oem-confirm-submit"
          >
            Record confirmation and apply
          </button>
        </div>
      </div>
    </div>
  );
};
